import { SaleType } from 'constants/filters/Sale';
import { NFTInterface } from 'models/NFT';
import { PAGINATION_SIZE } from 'models/request/Pagination';
import { IQuery } from 'models/request/Query';
import mockNfts from './nfts';

export interface MockNftQueryOptions {
  page: number;
  pageSize?: number;
  sort: string;
  category: string;
  forSale: boolean;
  saleTypes: SaleType[];
  queries: IQuery[];
  owner?: string;
}

const getQueryValue = (queries: IQuery[], key: string): string | undefined =>
  queries.find(query => query.key === key)?.value;

const matchesQueries = (nft: NFTInterface, queries: IQuery[]): boolean =>
  queries.every(query => {
    if (query.key === 'category' || query.key === 'sort' || query.key === 'owner') {
      return true;
    }

    if (query.key === 'subCategory') {
      return nft.subCategory.toLowerCase().startsWith(query.value.toLowerCase());
    }

    if (query.key === 'tokenId') {
      return nft.tokenId.toString() === query.value;
    }

    if (query.key.startsWith('attributes.')) {
      const attrKey = query.key.replace('attributes.', '').toUpperCase();
      const attrValue = nft.attributes?.[attrKey];
      if (!attrValue) return false;
      return query.value.split(',').some(value => attrValue.toLowerCase() === value.toLowerCase());
    }

    if (query.key === 'genders') {
      return nft.attributes?.GENDER?.toLowerCase() === query.value.toLowerCase();
    }

    if (query.key === 'types') {
      return nft.attributes?.TYPE?.toLowerCase() === query.value.toLowerCase();
    }

    if (query.key === 'rarities') {
      return nft.attributes?.RARITY?.toLowerCase() === query.value.toLowerCase();
    }

    if (query.key === 'structureTypes') {
      return nft.subCategory.toLowerCase() === query.value.toLowerCase();
    }

    if (query.key === 'resourceTypes') {
      return nft.subCategory.toLowerCase() === query.value.toLowerCase();
    }

    return true;
  });

const sortNfts = (items: NFTInterface[], sort: string): NFTInterface[] => {
  const sorted = [...items];

  sorted.sort((a, b) => {
    switch (sort) {
      case '+sale.price':
        return (a.sale?.sortPrice || 0) - (b.sale?.sortPrice || 0);
      case '-sale.price':
        return (b.sale?.sortPrice || 0) - (a.sale?.sortPrice || 0);
      case '+tokenId':
        return a.tokenId - b.tokenId;
      case '-tokenId':
        return b.tokenId - a.tokenId;
      case '+rarity':
        return (a.rarity?.rank || 0) - (b.rarity?.rank || 0);
      case '-rarity':
        return (b.rarity?.rank || 0) - (a.rarity?.rank || 0);
      default:
        return b.tokenId - a.tokenId;
    }
  });

  return sorted;
};

export const filterMockNfts = ({
  page,
  pageSize = PAGINATION_SIZE,
  sort,
  category,
  forSale,
  saleTypes,
  queries,
  owner,
}: MockNftQueryOptions): { list: NFTInterface[]; totalRecords: number } => {
  let filtered = mockNfts.filter(nft => nft.category === category);

  if (owner) {
    filtered = filtered.filter(nft => nft.owner.toLowerCase() === owner.toLowerCase());
  }

  const ownerQuery = getQueryValue(queries, 'owner');
  if (ownerQuery) {
    const normalizedOwner = ownerQuery.replace(/^string\((.*)\)$/, '$1');
    filtered = filtered.filter(nft => nft.owner.toLowerCase() === normalizedOwner.toLowerCase());
  }

  if (forSale) {
    filtered = filtered.filter(nft => nft.sale && nft.sale.status === 'live');
    if (saleTypes.length > 0) {
      filtered = filtered.filter(nft => saleTypes.includes(nft.sale.type as SaleType));
    }
  }

  filtered = filtered.filter(nft => matchesQueries(nft, queries));
  filtered = sortNfts(filtered, sort);

  const totalRecords = filtered.length;
  const start = (page - 1) * pageSize;
  const list = filtered.slice(start, start + pageSize);

  return { list, totalRecords };
};

export const getMockNftsByCategory = (category: string): NFTInterface[] =>
  mockNfts.filter(nft => nft.category === category);

export { mockNfts };
